import React, { useEffect } from 'react';
import { XCircle, Mail, Phone, Calendar, Globe, MapPin, Briefcase, FileText, Download, CheckCircle2, Clock } from 'lucide-react';

const statusPill = (v) => {
  const map = {
    pending: ['bg-[#FEF4DB]', 'text-[#8a5e00]', Clock, 'Pending'],
    approved: ['bg-[#E1F3E4]', 'text-[#0B5E2A]', CheckCircle2, 'Approved'],
    rejected: ['bg-[#FDE2E2]', 'text-[#9b1c1c]', XCircle, 'Rejected'],
  };
  const [bg, tx, Icon, label] = map[v] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${bg} ${tx}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </span>
  );
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="h-8 w-8 rounded-md bg-[#f0f6ee] flex items-center justify-center shrink-0"><Icon className="h-4 w-4 brand-green" /></div>
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#6b7e6d] font-semibold">{label}</div>
      <div className="text-sm text-[#1b2a1f]">{value}</div>
    </div>
  </div>
);

const ApplicationDrawer = ({ app, onClose, onUpdateStatus, onDownload }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed top-0 right-0 z-50 w-full max-w-lg h-full bg-white shadow-2xl overflow-y-auto">
        <div className="p-6 border-b border-[#0B5E2A]/10 sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#6b7e6d] font-semibold">Application</div>
              <h3 className="font-display text-2xl text-[#0B5E2A]">{app.name}</h3>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded hover:bg-[#f0f6ee] text-[#4b5e4d]">
              <XCircle className="h-5 w-5 pointer-events-none" />
            </button>
          </div>
          <div className="mt-3">{statusPill(app.status)}</div>
        </div>
        <div className="p-6 space-y-4">
          <Row icon={Mail} label="Email" value={app.email} />
          <Row icon={Phone} label="Phone" value={`${app.phone_code} ${app.phone}`} />
          <Row icon={Calendar} label="Date of Birth" value={app.dob} />
          <Row icon={Globe} label="Nationality" value={app.nationality} />
          <Row icon={MapPin} label="Status in Canada" value={app.status_in_canada} />
          <Row icon={Briefcase} label="Submitted" value={new Date(app.created_at).toLocaleString()} />
          <div>
            <div className="text-xs uppercase tracking-wider text-[#6b7e6d] font-semibold mb-2">Documents ({(app.documents || []).length})</div>
            {(app.documents || []).length === 0 ? (
              <div className="text-sm text-[#6b7e6d] italic">No documents uploaded.</div>
            ) : (
              <ul className="space-y-2">
                {(app.documents || []).map((d, i) => (
                  <li key={i} className="flex items-center gap-3 bg-[#f6faf3] border border-[#0B5E2A]/10 rounded-md px-3 py-2.5">
                    <FileText className="h-4 w-4 brand-green" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-sm font-medium">{d.name}</div>
                      <div className="text-xs text-[#6b7e6d]">{Math.ceil((d.size || 0)/1024)} KB · {d.type}</div>
                    </div>
                    <button type="button" onClick={() => onDownload(app.id, i, d.name)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border border-[#0B5E2A]/25 text-[#0B5E2A] font-semibold hover:bg-[#f0f6ee] transition-colors">
                      <Download className="h-3.5 w-3.5 pointer-events-none" /> Download
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="pt-4 flex gap-2 border-t border-[#0B5E2A]/10">
            <button type="button" onClick={() => onUpdateStatus(app.id, 'approved')} className="flex-1 btn-green px-4 py-2.5 rounded-md">Approve</button>
            <button type="button" onClick={() => onUpdateStatus(app.id, 'rejected')} className="flex-1 px-4 py-2.5 rounded-md bg-[#FDE2E2] text-[#9b1c1c] font-semibold hover:bg-[#fbcfcf] transition-colors">Reject</button>
            <button type="button" onClick={() => onUpdateStatus(app.id, 'pending')} className="flex-1 px-4 py-2.5 rounded-md bg-[#FEF4DB] text-[#8a5e00] font-semibold hover:bg-[#fce9b0] transition-colors">Pending</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ApplicationDrawer;
